<template>
    <div class="campaign-builder-modal">
        <GoalModal
            :is-open="isOpen"
            size="med"
            :action-button-label="actionButtonLabel"
            :summary-text="$t('funnel.communications.letter.summary')"
            :title="letterTitle"
            :goal-tip-text="$t('funnel.communications.letter.pro.tip')"
            :goal-tip-link="$t('funnel.communications.letter.pro.tip.link')"
            @close-modal="closeModal"
            @update-config="createOrEditLetter"
        />
    </div>
</template>

<script>
import GoalModal from '../../../whens/goalModal/GoalModal.vue';
import { createLetter, saveLetterSettings } from './letter.api';

const EVENT_READY_TOGGLE_CHANGED = 'letter-toggle-changed';
const EVENT_RENAME = 'letter-rename';
const EVENT_EDITOR_LOADED = 'letter-editor-loaded';
const EVENT_EDITOR_SAVE = 'letter-editor-save';
const EVENT_EDITOR_CONFIG_CHANGED = 'letter-editor-config-changed';
const EVENT_TYPE_NAME_CHANGED = 'letter-name-changed';
const EVENT_TYPE_READY_TOGGLE_CHANGED = 'letter-ready-toggle-changed';

export const PROCESS_NAME = 'Send a letter';
export const PROCESS_TYPE = 'letter';

export default {
    PROCESS_NAME,
    PROCESS_TYPE,
    components: {
        GoalModal,
    },
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        funnelId: {
            type: [String, Number],
            required: true,
        },
        cell: {
            type: Object,
            default: () => ({
                value: {
                    marketingLetterId: '',
                },
            }),
        },
    },
    emits: ['close-modal', 'update-cell-value', 'update-cell-name'],

    data() {
        return {
            marketingLetterId: '0',
            editorsTab: null,
            isReady: false,
        };
    },

    computed: {
        actionButtonLabel() {
            return this.isConfigured ? this.$t('funnel.communications.letter.edit') : this.$t('funnel.communications.letter.create');
        },

        isConfigured() {
            return this.marketingLetterId !== '0';
        },

        letterTitle() {
            return this.cell.value?.name ?? PROCESS_NAME;
        },
    },

    mounted() {
        this.addEventListeners();
        this.load();
    },

    unmounted() {
        this.removeEventListeners();
    },

    methods: {
        load() {
            this.loadCellValue();
        },

        loadCellValue() {
            const cell = { ...this.cell };

            cell.value = cell.value || {};

            this.marketingLetterId = cell?.value?.marketingLetterId ?? '0';

            this.isReady = cell?.value?.ready === '1' ?? false;
        },

        addEventListeners() {
            window.addEventListener(EVENT_READY_TOGGLE_CHANGED, this.readyToggleChanged);
            window.addEventListener(EVENT_EDITOR_LOADED, this.editorReady);
            window.addEventListener(EVENT_RENAME, this.editorRename);
            window.addEventListener(EVENT_EDITOR_SAVE, this.editorSave);
            window.addEventListener(EVENT_EDITOR_CONFIG_CHANGED, this.editorConfigChanged);
        },

        removeEventListeners() {
            window.removeEventListener(EVENT_READY_TOGGLE_CHANGED, this.readyToggleChanged);
            window.removeEventListener(EVENT_EDITOR_LOADED, this.editorReady);
            window.removeEventListener(EVENT_RENAME, this.editorRename);
            window.removeEventListener(EVENT_EDITOR_SAVE, this.editorSave);
            window.removeEventListener(EVENT_EDITOR_CONFIG_CHANGED, this.editorConfigChanged);
        },

        readyToggleChanged() {
            this.isReady = !this.isReady;

            this.saveCell();
        },

        saveCell() {
            const cell = { ...this.cell };

            cell.value = cell.value || {};
            cell.value.marketingLetterId = this.marketingLetterId;

            this.$emit('update-cell-value', { cell, isReady: this.isReady, preventClose: true });
        },

        editorReady() {
            this.dispatchEditorEvent(EVENT_TYPE_READY_TOGGLE_CHANGED, { ready: this.isReady });
            this.dispatchEditorEvent(EVENT_TYPE_NAME_CHANGED, { name: this.letterTitle });
        },

        dispatchEditorEvent(eventType, detail = {}) {
            if (this.editorsTab) {
                const message = {
                    eventType,
                    ...detail,
                };

                this.editorsTab.postMessage(message, '*');
            }
        },

        editorRename({ detail }) {
            const newName = detail.name;

            if (newName) {
                const cell = { ...this.cell };

                cell.value = cell.value || {};
                cell.value.marketingLetterId = this.marketingLetterId;
                cell.value.name = newName;

                this.$emit('update-cell-name', { cell });
            }

            this.dispatchEditorEvent(EVENT_TYPE_NAME_CHANGED, { name: this.letterTitle });
        },

        editorSave() {
            this.saveCell();
        },

        editorConfigChanged(event) {
            if (this.isConfigured) {
                const assignedUserId = event.detail?.assignedUserId;
                const sendNotification = event.detail?.sendNotification ?? false;

                saveLetterSettings(this.marketingLetterId, assignedUserId, sendNotification);
            }
        },

        closeModal() {
            if (this.editorsTab && !this.editorsTab.closed) {
                this.editorsTab.focus();
            } else {
                this.$emit('close-modal');
            }
        },

        async createOrEditLetter() {
            try {
                if (!this.isConfigured) {
                    this.marketingLetterId = await this.createLetter();
                    this.saveCell();
                }

                this.editLetter();
            } catch (error) {
                const message = `${this.$t('funnel.communications.letter.errorCreate')} ${error}`;

                this.$toast({ message });
            }
        },

        async createLetter() {
            return createLetter();
        },

        editLetter() {
            if (this.editorsTab && !this.editorsTab.closed) {
                this.editorsTab.focus();
            } else {
                const editorUrl = `/app/letterFunnel/standAloneEditor?contentType=${PROCESS_TYPE}&parentId=${this.marketingLetterId}`;

                this.editorsTab = window.open(editorUrl, '_blank');
            }
        },
    },
};
</script>
